import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Undo2, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { PageHeader, LoadingState } from '@/components/common';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import { stagger, fadeUp } from '@/lib/animations';
import { rentalsApi, type RentalDetail as RentalDetailData } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatCurrency, formatDate } from '@/lib/utils';

/**
 * One rental, in full.
 *
 * The list carries what you scan; this carries what you quote on the phone.
 * "Coiled tubing reel and injector head" names a category — the lessor has
 * several — so the make, the model, their serial and their own ticket number
 * are what actually finds the tool at their end.
 *
 * Three references, and they are not interchangeable, so the page keeps them
 * apart: our purchase order identifies the *hire* and may cover several tools;
 * the lessor's reference identifies the tool on their paperwork; the serial
 * identifies the physical unit. Somebody chasing this needs to know which one
 * they are reading out.
 */
/** One reference, with a line saying which of the three it is. */
function Reference({ label, value, hint }: { label: string; value: string | null; hint: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        {value ? (
          <span className="font-mono text-sm">{value}</span>
        ) : (
          <span className="text-sm text-status-warning-foreground">Not recorded</span>
        )}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export default function RentalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rental, setRental] = useState<RentalDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoised so the effect can depend on it honestly rather than lying about
  // its dependencies — the action handlers below reuse the same function.
  const load = useCallback(() => {
    if (!id) return Promise.resolve();
    return rentalsApi
      .getById(id)
      .then(setRental)
      .catch((error) => {
        toast.error(getErrorMessage(error));
        navigate('/equipment/rentals');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const recordReturn = async () => {
    if (!rental) return;
    try {
      await rentalsApi.recordOffHire(rental.id);
      toast.success('Recorded as returned');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) return <LoadingState variant="detail" />;
  if (!rental) return null;

  const onHire = !rental.offHireAt;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Equipment', href: '/equipment' },
            { label: 'Rentals', href: '/equipment/rentals' },
            { label: rental.externalReference },
          ]}
          title={rental.description}
          description={`${rental.lessor}${rental.serialNumber ? ` · s/n ${rental.serialNumber}` : ''}`}
          actions={
            onHire ? (
              <Button onClick={recordReturn}>
                <Undo2 className="mr-2 h-4 w-4" />
                Record return
              </Button>
            ) : undefined
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard
                title="The unit"
                icon={<Wrench className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Description', value: rental.description },
                    {
                      label: 'Manufacturer',
                      value: rental.manufacturer,
                      when: !!rental.manufacturer,
                    },
                    { label: 'Model', value: rental.model, when: !!rental.model },
                    {
                      label: 'Serial',
                      value: rental.serialNumber,
                      when: !!rental.serialNumber,
                    },
                    {
                      // Only set where we also carry the tool in our own
                      // register, which is unusual — it is not our asset.
                      label: 'Our asset',
                      value: rental.equipment?.assetNumber,
                      when: !!rental.equipment,
                    },
                    {
                      label: 'Where',
                      value: rental.location?.name,
                      when: !!rental.location,
                    },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Lessor"
                icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    {
                      label: 'Company',
                      value: rental.supplier ? (
                        <Link
                          to={`/suppliers/${rental.supplier.id}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {rental.lessor}
                        </Link>
                      ) : (
                        rental.lessor
                      ),
                    },
                    {
                      label: 'Contact',
                      value: rental.supplier?.contactName,
                      when: !!rental.supplier?.contactName,
                    },
                    {
                      label: 'Phone',
                      value: rental.supplier?.phone,
                      when: !!rental.supplier?.phone,
                    },
                    {
                      label: 'Email',
                      value: rental.supplier?.email,
                      when: !!rental.supplier?.email,
                    },
                  ]}
                />
                {!rental.supplier && (
                  // A specialty tool house engaged for one job often is not a
                  // registered supplier. Saying so beats an almost-empty card
                  // that reads as missing data.
                  <p className="mt-2 border-t pt-2 text-xs text-muted-foreground">
                    Not held as a supplier, so no contact details here. They are on the agreement in
                    the ERP.
                  </p>
                )}
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Where it stands</h3>
                      <CardDescription className="mt-1">{rental.statement}</CardDescription>
                    </div>
                    {rental.needsAction && <Badge variant="destructive">Needs action</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  <DetailList
                    items={[
                      { label: 'On hire since', value: formatDate(rental.onHireAt) },
                      { label: 'Days on hire', value: rental.daysOnHire },
                      {
                        label: 'Due back',
                        value: rental.expectedOffHireAt
                          ? formatDate(rental.expectedOffHireAt)
                          : 'Not set — nothing will chase it',
                        tone: rental.expectedOffHireAt ? undefined : 'warning',
                      },
                      {
                        label: 'Returned',
                        value: rental.offHireAt ? formatDate(rental.offHireAt) : null,
                        when: !!rental.offHireAt,
                      },
                      {
                        label: 'Day rate',
                        // As recorded on the agreement. What is owed is settled
                        // in the ERP and a second figure here would be acted on
                        // by whoever saw it first.
                        value:
                          rental.dayRate != null
                            ? `${formatCurrency(rental.dayRate, rental.currency)} as recorded on the agreement`
                            : null,
                        when: rental.dayRate != null,
                      },
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <h3 className="text-base font-semibold text-foreground">References</h3>
                  <CardDescription>
                    Three different numbers. One identifies the hire, one identifies the tool on
                    the lessor&rsquo;s paperwork, and the serial identifies the physical unit.
                  </CardDescription>
                </CardHeader>
                {/* Rendered bespoke rather than through DetailList: each row
                    needs a line saying what the number is for, and without it
                    three similar-looking references are easy to read out in the
                    wrong order. */}
                <CardContent className="space-y-3">
                  <Reference
                    label="Our purchase order"
                    value={rental.externalReference}
                    hint="Raised and settled in the ERP. May cover more than this one tool."
                  />
                  <Reference
                    label="Lessor's reference"
                    value={rental.lessorReference}
                    hint="What they will ask for when you call."
                  />
                  <Reference
                    label="Serial number"
                    value={rental.serialNumber}
                    hint="Identifies this unit rather than the category."
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <h3 className="text-base font-semibold text-foreground">Hired for</h3>
                </CardHeader>
                <CardContent>
                  {rental.job ? (
                    <DetailList
                      items={[
                        {
                          label: 'Job',
                          value: (
                            <Link
                              to={`/jobs/${rental.job.id}`}
                              className="underline-offset-4 hover:underline"
                            >
                              {rental.job.jobNumber} — {rental.job.name}
                            </Link>
                          ),
                        },
                        { label: 'Status', value: rental.job.status.toLowerCase() },
                        {
                          label: 'Well',
                          value: rental.job.well ? (
                            <Link
                              to={`/wells/${rental.job.well.id}`}
                              className="underline-offset-4 hover:underline"
                            >
                              {rental.job.well.name}
                            </Link>
                          ) : null,
                          when: !!rental.job.well,
                        },
                        {
                          label: 'Customer',
                          value: rental.job.customerName,
                          when: !!rental.job.customerName,
                        },
                        { label: 'AFE', value: rental.job.afeNumber, when: !!rental.job.afeNumber },
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Not linked to a job, so nothing here can tell whether the work it came in for
                      has finished.
                    </p>
                  )}
                </CardContent>
              </Card>

              {rental.notes && (
                <Card>
                  <CardHeader className="pb-3">
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{rental.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
